import React, { useState } from 'react'
import { api } from '../../utils/api'
import { resolvedAddressFields } from '../../utils/carrierAssignment'

/**
 * Correct a collection's pickup address using the structured fields Prism keeps
 * authoritative.
 *
 * Customers often measure the item, not the packed parcel, so the auto-captured
 * address needs fixing after submission. Read-only until Edit; on save it PATCHes
 * the structured columns (line_1/line_2/town/postcode) and updates the shipment in
 * place so the panel reflects the correction without a reload. When the collection
 * is already booked, it warns that the courier order will not update itself.
 */
export default function CollectionAddressEditor({ shipment, isBooked, onSaved }) {
  const current = resolvedAddressFields(shipment)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function startEdit() {
    setForm({
      line_1: current.line1 || '',
      line_2: current.line2 || '',
      town: current.city || '',
      postcode: current.postcode || '',
    })
    setError(null)
    setEditing(true)
  }

  function setField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function save() {
    const line1 = form.line_1.trim()
    const postcode = form.postcode.trim()
    if (!line1 || !postcode) {
      setError('Address line 1 and postcode are required')
      return
    }

    const payload = {
      address_line_1: line1,
      address_line_2: form.line_2.trim(),
      address_town: form.town.trim(),
      address_postcode: postcode,
    }
    // Preserve the deal's own country rather than force GB — the editor has no
    // country field, so pass through what Prism already holds.
    const country = shipment.resolved_address?.country
    if (country) payload.address_country_code = country

    setSaving(true)
    setError(null)
    try {
      await api.patch(`/api/deals/collections/${shipment.id}/collection-address`, payload)
      applyLocally(shipment, payload)
      setEditing(false)
      onSaved?.()
    } catch (e) {
      setError(`Failed to save: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontSize: 13 }}>
          <div>{current.line1 || '—'}</div>
          {current.line2 && <div>{current.line2}</div>}
          {current.city && <div>{current.city}</div>}
          <div style={{ fontFamily: 'monospace' }}>{current.postcode || ''}</div>
        </div>
        <button
          className="btn btn-xs"
          onClick={startEdit}
          style={{ fontSize: 10, padding: '2px 8px', background: '#333', color: 'white', border: 'none' }}
        >
          Edit
        </button>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: 'var(--space-2)' }}>
      {isBooked && (
        <div style={{ fontSize: 11, color: 'var(--color-warning, #b45309)', marginBottom: 6 }}>
          This collection is already booked. After saving, re-book the pickup or re-print the label — the courier order will not update itself.
        </div>
      )}
      <AddressField label="Address line 1" value={form.line_1} onChange={v => setField('line_1', v)} />
      <AddressField label="Address line 2" value={form.line_2} onChange={v => setField('line_2', v)} />
      <AddressField label="Town" value={form.town} onChange={v => setField('town', v)} />
      <AddressField label="Postcode" value={form.postcode} onChange={v => setField('postcode', v)} mono />
      {error && <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 4 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-xs" onClick={() => setEditing(false)} style={{ fontSize: 10, padding: '3px 8px' }}>
          Cancel
        </button>
        <button
          className="btn btn-xs"
          onClick={save}
          disabled={saving}
          style={{ fontSize: 10, padding: '3px 8px', background: '#333', color: 'white', border: 'none' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}

/** Mirror Prism's write locally: structured columns win, marked manual + verified. */
function applyLocally(shipment, payload) {
  shipment.resolved_address = {
    line_1: payload.address_line_1,
    line_2: payload.address_line_2,
    town: payload.address_town,
    county: shipment.resolved_address?.county || '',
    postcode: payload.address_postcode,
    country: payload.address_country_code || shipment.resolved_address?.country || 'GB',
    // A typed correction is manual + unverified, matching the collection form's
    // manual-entry path; udprn/uprn drop with it.
    verified: false,
    source: 'manual',
    udprn: null,
    uprn: null,
  }
  shipment.collection_address = [
    payload.address_line_1,
    payload.address_line_2,
    payload.address_town,
    payload.address_postcode,
  ].filter(Boolean).join(', ')
}

function AddressField({ label, value, onChange, mono }) {
  const id = `addr-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <label htmlFor={id} style={{ fontSize: 11, color: 'var(--color-text-muted)', minWidth: 90 }}>{label}</label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ flex: 1, padding: '3px 6px', fontSize: 12, borderRadius: 4, border: '1px solid var(--color-border)', fontFamily: mono ? 'monospace' : 'inherit' }}
      />
    </div>
  )
}
